import React, { useEffect, useState } from 'react';
import { collection, getDocs } from 'firebase/firestore';
import { db } from '../../../config/firebase';
import { AgeCategory } from '../../../types/camper';

interface YouthCamperRecord {
  id: string;
  firstName: string;
  lastName: string;
  ageCategory: string;
  subCategory: string;
  placeName: string;
}

type SortKey = keyof YouthCamperRecord;
type SortDirection = 'asc' | 'desc';

const columns: { key: SortKey; label: string }[] = [
  { key: 'firstName', label: 'First Name' },
  { key: 'lastName', label: 'Last Name' },
  { key: 'ageCategory', label: 'Age Category' },
  { key: 'subCategory', label: 'Sub Category' },
  { key: 'placeName', label: 'Place Name' },
];

const YouthBreakDown: React.FC = () => {
  const [records, setRecords] = useState<YouthCamperRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [sortKey, setSortKey] = useState<SortKey>('lastName');
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc');

  useEffect(() => {
    const fetchYouthCampers = async () => {
      try {
        setLoading(true);
        setError(null);
        const snapshot = await getDocs(collection(db, 'Registrations'));
        const youthRecords: YouthCamperRecord[] = [];
        snapshot.forEach(docSnap => {
          const data = docSnap.data();
          // Only include registrations that have been assigned a place (exclude Pending, etc.)
          if (data.status !== 'Assigned') {
            return;
          }
          const placeName = data.assignedAccommodation?.placeName || data.accommodationFees?.placeName || data.financial?.accommodationFees?.placeName || '';
          if (data.attendingCampers && Array.isArray(data.attendingCampers)) {
            data.attendingCampers.forEach((camper: any, idx: number) => {
              const ageCategory = camper.age || camper.category;
              if (ageCategory === AgeCategory.YouthChildren) {
                youthRecords.push({
                  id: `${docSnap.id}-${idx}`,
                  firstName: camper.firstName || '',
                  lastName: camper.lastName || '',
                  ageCategory: ageCategory || '',
                  subCategory: camper.YouthChildrenCategory || camper.youthChildrenCategory || camper.youthChildrenSubCategory || 'Not Specified',
                  placeName,
                });
              }
            });
          }
        });
        setRecords(youthRecords);
      } catch (err) {
        setError('Failed to load youth breakdown report');
      } finally {
        setLoading(false);
      }
    };
    fetchYouthCampers();
  }, []);

  const handleSort = (key: SortKey) => {
    if (sortKey === key) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortKey(key);
      setSortDirection('asc');
    }
  };

  const sortedRecords = [...records].sort((a, b) => {
    const aValue = a[sortKey] || '';
    const bValue = b[sortKey] || '';
    if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1;
    if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1;
    return 0;
  });

  const renderSortIndicator = (key: SortKey) => {
    if (sortKey !== key) return null;
    return sortDirection === 'asc' ? ' ▲' : ' ▼';
  };

  // CSV Export
  const handleExportCSV = () => {
    const headers = columns.map(col => col.label);
    const rows = sortedRecords.map(record => [
      record.firstName,
      record.lastName,
      record.ageCategory,
      record.subCategory,
      record.placeName,
    ]);
    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.map(field => `"${String(field).replace(/"/g, '""')}"`).join(',')),
    ].join('\r\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', 'youth-breakdown.csv');
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  return (
    <div className="youth-breakdown-report" style={{ maxWidth: 900, margin: '2rem auto', padding: 24, border: '1px solid #ccc', borderRadius: 8 }}>
      <h2>Youth Break Down</h2>
      <button onClick={handleExportCSV} style={{ marginBottom: 16 }}>Export to CSV</button>
      {loading && <div>Loading youth breakdown...</div>}
      {error && <div style={{ color: 'red' }}>{error}</div>}
      {!loading && !error && (
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr>
              {columns.map(col => (
                <th
                  key={col.key}
                  style={{ border: '1px solid #ccc', padding: 8, cursor: 'pointer', userSelect: 'none' }}
                  onClick={() => handleSort(col.key)}
                >
                  {col.label}
                  {renderSortIndicator(col.key)}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {sortedRecords.map(record => (
              <tr key={record.id}>
                <td style={{ border: '1px solid #ccc', padding: 8 }}>{record.firstName}</td>
                <td style={{ border: '1px solid #ccc', padding: 8 }}>{record.lastName}</td>
                <td style={{ border: '1px solid #ccc', padding: 8 }}>{record.ageCategory}</td>
                <td style={{ border: '1px solid #ccc', padding: 8 }}>{record.subCategory}</td>
                <td style={{ border: '1px solid #ccc', padding: 8 }}>{record.placeName}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      {!loading && !error && records.length === 0 && (
        <div style={{ marginTop: 16 }}>No youth/children found.</div>
      )}
    </div>
  );
};

export default YouthBreakDown; 